// Wishlist Page - View and manage saved products
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWishlist } from '@/context/WishlistContext';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';

export default function WishlistPage() {
  const { wishlist, removeFromWishlist, clearWishlist, isAuthenticated, loading } = useWishlist();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();

  const handleAddToCart = async (item) => {
    const result = await addToCart(item.id);
    if (result?.success) toast.success('Added to cart');
    else toast.error(result?.error || 'Could not add to cart');
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="wishlist-signin-prompt">
        <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Sign in to see your wishlist</h1>
        <p className="text-muted-foreground mb-8">Your saved items follow you to any device once you sign in.</p>
        <Link to="/login"><Button size="lg" data-testid="wishlist-signin-btn">Sign in</Button></Link>
      </div>
    );
  }

  if (wishlist.length === 0 && !loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Your wishlist is empty</h1>
        <p className="text-muted-foreground mb-8">
          Save items you love by clicking the heart icon on any product
        </p>
        <Link to="/products">
          <Button size="lg" data-testid="wishlist-browse-btn">
            Browse Products
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-500 fill-red-500" />
            My Wishlist
          </h1>
          <p className="text-muted-foreground">{wishlist.length} items saved</p>
        </div>
        {wishlist.length > 0 && (
          <Button variant="outline" onClick={clearWishlist} data-testid="wishlist-clear-btn">
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {wishlist.map((item) => (
          <div 
            key={item.id} 
            className="bg-card border rounded-xl overflow-hidden group"
            data-testid={`wishlist-item-${item.id}`}
          >
            <Link to={`/products/${item.id}`}>
              <div className="aspect-square relative overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
            </Link>
            
            <div className="p-4">
              <Link to={`/products/${item.id}`}>
                <h3 className="font-semibold mb-1 hover:text-primary truncate">
                  {item.name}
                </h3>
              </Link>
              <p className="text-lg font-bold text-primary mb-4">
                {formatPrice(item.price)}
              </p>
              
              <div className="flex gap-2">
                {item.product_type === 'physical' && (
                  <Button 
                    onClick={() => handleAddToCart(item)}
                    data-testid={`wishlist-add-cart-${item.id}`}
                    className="flex-1"
                    size="sm"
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Add to Cart
                  </Button>
                )}
                {item.product_type === 'affiliate' && (
                  <Link to={`/products/${item.id}`} className="flex-1">
                    <Button className="w-full" size="sm">
                      View Deal
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeFromWishlist(item.id)}
                  data-testid={`wishlist-remove-${item.id}`}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
